import { Box, CircularProgress, Typography } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import { BryntumScheduler } from "@bryntum/scheduler-react";
import _ from "lodash";

import { AppState } from "store/types";
import { useSelector } from "react-redux";

import "./CalendarStyles.scss";
import { DateHelper } from "@bryntum/scheduler";
interface CalendarProps {
  data: any;
  isFetching: boolean;
}

const mapResources = (records: any) => {
  const resources = [] as any;
  if (records) {
    records.forEach((record: any, i: number) => {
      resources.push({
        id: i,
        name: `${record.asin} ${record.seller_name}`,
      });
    });
  }
  return resources;
};

const mapEvents = (records: any, timeUnit: string) => {
  const events = [] as any;
  if (records) {
    records.forEach((record: any, i: number) => {
      record.event.forEach((event: any) => {
        let cls = "";
        if (event.type === "7_DAY_DEAL") {
          cls = "seven-day-deal-event";
        } else if (event.type === "COUPON") {
          cls = "coupon-event";
        } else if (event.type === "PRIME_EXCLUSIVE_DEAL") {
          cls = "prime-exclusive-deal-event";
        } else if (event.type === "LIGHTNING_DEAL") {
          cls = "lightning-deal-event";
        } else if (event.type === "PROMOTION_CODE") {
          cls = "promotion-code-event";
        }

        events.push({
          resourceId: i,
          startDate: event.date,
          endDate: event.eddate,
          durationUnit: timeUnit,
          name: event.total_quantity,
          cls: cls,
        });
      });
    });
  }
  return events;
};

function Calendar({ data, isFetching }: CalendarProps) {
  const scheduler = useRef<BryntumScheduler>(null);
  const { timeUnit } = useSelector(
    (state: AppState) => state.promotionCalendar
  );

  const [config, setConfig] = useState<any>({
    autoHeight: true,
    rowHeight: 62,
    readOnly: true,
    autoAdjustTimeAxis: true,
    zoomOnTimeAxisDoubleClick: false,
    columns: [
      {
        text: "",
        field: "name",
        width: 160,
        renderer: ({ record }: any) => {
          const name = record.name; // Get the resource name from the record

          // Split the resource name into two parts based on the space separator
          const [part1, part2] = name.split(" ");

          // Create a div element containing two child elements for each part
          const div = document.createElement("div");
          const p1 = document.createElement("p");
          const p2 = document.createElement("p");

          p1.classList.add("part1");
          p1.innerHTML = part1;
          p1.setAttribute(
            "style",
            'margin: 0; font-family: "Lato"; font-style: normal; font-weight: 400; font-size: 16px; line-height: 150%; letter-spacing: 0.15px; color: #424242;'
          );

          p2.classList.add("part2");
          p2.innerHTML = part2;
          p2.setAttribute(
            "style",
            'margin: 0; font-family: "Lato"; font-style: normal; font-weight: 400; font-size: 12px; line-height: 14px; color: #757575;'
          );

          div.appendChild(p1);
          div.appendChild(p2);

          return div;
        },
      },
    ],
    viewPreset: {
      base: "weekAndDay",
      headers: [
        {
          unit: "week",
          align: "center",
          renderer: (header: any) => {
            const startDate = new Date(header) as any;
            const yearStart = new Date(startDate.getFullYear(), 0, 1) as any;
            const weekNumber = Math.ceil(
              ((startDate - yearStart) / 86400000 + yearStart.getDay() + 1) / 7
            );
            const endDate = new Date(startDate.getTime() + 6 * 86400000);
            const weekNumberHtml = `<span style="font-family: 'Lato'; font-style: normal; font-weight: 400; font-size: 14px; line-height: 17px; color: #424242;">Week ${weekNumber}</span>`;
            const dateRangeHtml = `<span style="font-family: 'Lato'; font-style: normal; font-weight: 400; font-size: 10px; line-height: 12px; color: #757575;">${startDate.toLocaleDateString(
              "default",
              { month: "short", day: "numeric" }
            )} - ${endDate.toLocaleDateString("default", {
              month: "short",
              day: "numeric",
            })}</span>`;
            return `<div style="padding: 10px; display: flex; gap: 8px; flex-direction: column; justify-content: center; align-items: center;">${weekNumberHtml}${dateRangeHtml}</div>`;
          },
        },
      ],
    },

    features: {
      scheduleTooltip: false,
      eventEdit: {
        disabled: true,
      },
    },

    resources: [],
    events: [],

    startDate: new Date(),
    endDate: new Date(),
  });

  useEffect(() => {
    window.addEventListener("error", (e) => {
      if (e.message === "ResizeObserver loop limit exceeded") {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    });
  }, []);

  useEffect(() => {
    if (!_.isEmpty(data)) {
      const newSchedulerConfig = { ...config };

      let resources = mapResources(data);
      let events = mapEvents(data, timeUnit);

      if (events.length > 0) {
        let earliestStartDate = events[0].startDate;
        let latestEndDate = events[0].endDate;

        for (let i = 1; i < events.length; i++) {
          const task = events[i];
          if (task.startDate < earliestStartDate) {
            earliestStartDate = task.startDate;
          }
          if (task.endDate > latestEndDate) {
            latestEndDate = task.endDate;
          }
        }

        newSchedulerConfig.startDate = new Date(earliestStartDate);
        newSchedulerConfig.endDate = new Date(latestEndDate);
      }

      if (timeUnit === "week") {
        newSchedulerConfig.viewPreset.headers = [
          {
            unit: "week",
            align: "center",
            renderer: (header: any) => {
              const startDate = new Date(header) as any;
              const yearStart = new Date(startDate.getFullYear(), 0, 1) as any;
              const weekNumber = Math.ceil(
                ((startDate - yearStart) / 86400000 + yearStart.getDay() + 1) /
                  7
              );
              const endDate = new Date(startDate.getTime() + 6 * 86400000);
              const weekNumberHtml = `<span style="font-family: 'Lato'; font-style: normal; font-weight: 400; font-size: 14px; line-height: 17px; color: #424242;">Week ${weekNumber}</span>`;
              const dateRangeHtml = `<span style="font-family: 'Lato'; font-style: normal; font-weight: 400; font-size: 10px; line-height: 12px; color: #757575;">${startDate.toLocaleDateString(
                "default",
                { month: "short", day: "numeric" }
              )} - ${endDate.toLocaleDateString("default", {
                month: "short",
                day: "numeric",
              })}</span>`;
              return `<div style="padding: 10px; display: flex; gap: 8px; flex-direction: column; justify-content: center; align-items: center;">${weekNumberHtml}${dateRangeHtml}</div>`;
            },
          },
        ];
      } else if (timeUnit === "day") {
        newSchedulerConfig.viewPreset.headers = [
          {
            unit: "day",
            increment: 1,
            align: "center",
            renderer: (header: any) => {
              const options = { month: "short", day: "numeric" };
              const date = header.toLocaleString(undefined, options);
              const year = header.toLocaleString(undefined, {
                year: "numeric",
              });
              return `
        <div style="display: flex; gap: 5px; flex-direction: column; justify-content: center; align-items: center;">
          <span style="font-family: 'Lato'; font-style: normal; font-weight: 400; font-size: 14px; line-height: 14px; color: #424242;">${date}</span>
          <span style="font-family: 'Lato'; font-style: normal; font-weight: 400; font-size: 14px; line-height: 14px; color: #757575;">${year}</span>
       </div> `;
            },
          },
        ];
        newSchedulerConfig.viewPreset.base = "dayAndWeek";
      } else {
        newSchedulerConfig.viewPreset.headers = [
          {
            unit: "month",
            increment: 1,
            align: "center",
            renderer: (header: any) => {
              const formatter = new Intl.DateTimeFormat(undefined, {
                month: "short",
                year: "numeric",
              });
              const formattedDate = formatter.format(header);
              return `
        <div style="display: flex; gap: 5px; flex-direction: column; justify-content: center; align-items: center;">
          <span style="font-family: 'Lato'; font-style: normal; font-weight: 400; font-size: 14px; line-height: 17px; color: #424242;">${formattedDate}</span>
        </div> `;
            },
          },
        ];
        newSchedulerConfig.viewPreset.base = "dayAndWeek";
      }

      newSchedulerConfig.scrollLeft = 0;
      newSchedulerConfig.scrollTop = 0;
      newSchedulerConfig.resources = resources;
      newSchedulerConfig.events = events;
      newSchedulerConfig.zoomOnMouseWheel = false;
      newSchedulerConfig.zoomOnTimeAxisDoubleClick = false;
      newSchedulerConfig.listeners = {
        zoom: (event: any) => {
          event.preventDefault();
        },
      };

      newSchedulerConfig.features = {
        ...config.features,
        eventTooltip: {
          align: "l-r",
          template: (data: any) => `<div style="background-color: #f3f4f5;">
             <p style="margin: 0; padding: 0; background-color: #f3f4f5;">
                 ${DateHelper.format(
                   data.eventRecord.startDate,
                   "D MMM YYYY"
                 )} - ${DateHelper.format(
            data.eventRecord.endDate,
            "D MMM YYYY"
          )}
             </p>
         </div>`,
        },
      };
      setConfig(newSchedulerConfig);
    }
  }, [data]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexGrow: 1,
      }}
    >
      {isFetching ? (
        <CircularProgress
          sx={{
            zIndex: 2,
            width: "40px",
            height: "40px",
            position: "absolute",
            right: 0,
            top: "243px",
            left: 0,
            margin: "0 auto",
          }}
        />
      ) : data.length > 0 ? (
        <BryntumScheduler ref={scheduler} config={config} maskDefaults={{}} />
      ) : (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Lato",
              fontSize: "15px",
              color: "grey",
              letterSpacing: "0px",
              textAlign: "center",
            }}
          >
            No Data Available
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default Calendar;
